import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button, Card, Toggle } from 'react-daisyui';
import { Users, Clock, Activity, Database } from 'react-feather';
import Sidebar from './Sidebar';
import BlockUptimeSquares from './BlockUptimeSquares';
import ELValidatorsTable from './ELValidatorsTable';
import TopNavbar from './TopNavbar';
import ValidatorSearch from './ValidatorSearch';

const CL_API_ENDPOINT = 'https://berachain-v2-blacknodes-api.blacknodes.net';
const CL_RPC_ENDPOINT = 'https://berachain-v2-testnet-rpc.blacknodes.net';
const EL_API_ENDPOINT = 'https://berachain-v2-validators.blacknodes.net/api';

const StatCard = ({ name, value, icon: Icon }) => (
  <Card className="bg-gray-900 border border-gray-800">
    <Card.Body>
      <div className="flex items-center mb-2">
        <Icon className="text-cyan-400 mr-2" size={24} />
        <Card.Title tag="h2">{name}</Card.Title>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </Card.Body>
  </Card>
);

const ValidatorsPage = () => {
  const [layer, setLayer] = useState('cl');
  const [clValidators, setClValidators] = useState([]);
  const [elValidators, setElValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    latestBlockHeight: 0,
    totalValidators: 0,
    averageUptime: 0,
    totalBgtStaked: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [missedBlocksData, setMissedBlocksData] = useState([]);
  const [loadingMissedBlocks, setLoadingMissedBlocks] = useState(true);

  const fetchClValidators = useCallback(async (currentPage) => {
    const [validatorsResponse, statusResponse] = await Promise.all([
      axios.get(`${CL_API_ENDPOINT}/validators/recent-blocks`, { params: { page: currentPage, per_page: 10 } }),
      axios.get(`${CL_RPC_ENDPOINT}/status`),
    ]);

    const validatorsData = validatorsResponse.data.validators;
    const latestBlockHeight = parseInt(statusResponse.data.result.sync_info.latest_block_height);

    setTotalPages(validatorsResponse.data.total_pages);
    setLastUpdated(validatorsResponse.data.last_updated);

    setStats(prevStats => ({
      ...prevStats,
      latestBlockHeight,
      totalValidators: validatorsResponse.data.total_validators,
    }));

    return validatorsData;
  }, []);

  const fetchMissedBlocks = useCallback(async (currentPage) => {
    setLoadingMissedBlocks(true);
    try {
      const missedBlocksResponse = await axios.get(`${CL_API_ENDPOINT}/validators/missed-blocks`, { params: { page: currentPage, per_page: 10 } });
      const missedBlocksData = missedBlocksResponse.data.missed_blocks;
      setMissedBlocksData(missedBlocksData);

      const averageUptime = missedBlocksData.reduce((sum, v) => 
        sum + (100 - v.missed_percentage), 0
      ) / missedBlocksData.length;

      setStats(prevStats => ({
        ...prevStats,
        averageUptime: averageUptime.toFixed(2),
      }));

      setLoadingMissedBlocks(false);
    } catch (error) {
      console.error('Error fetching missed blocks data:', error);
      setLoadingMissedBlocks(false);
    }
  }, []);

  const fetchElValidators = useCallback(async () => {
    const validatorsResponse = await axios.get(`${EL_API_ENDPOINT}/validators`);
    const validatorsData = validatorsResponse.data;

    setStats(prevStats => ({
      ...prevStats,
      latestBlockHeight: 'N/A',
      totalValidators: validatorsData.length,
      averageUptime: 0,
      totalBgtStaked: validatorsData.reduce((sum, v) => sum + v.amountStaked, 0),
    }));

    return validatorsData;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (layer === 'cl') {
        const newValidators = await fetchClValidators(page);
        setClValidators(newValidators);
      } else {
        const newValidators = await fetchElValidators();
        setElValidators(newValidators);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load validator data. Please try again later.');
      setLoading(false);
    }
  }, [layer, page, fetchClValidators, fetchElValidators]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (layer === 'cl' && !loading) {
      fetchMissedBlocks(page);
    }
  }, [layer, page, loading, fetchMissedBlocks]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData();
      if (layer === 'cl') {
        fetchMissedBlocks(page);
      }
    }, 60000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [fetchData, fetchMissedBlocks, layer, page]);

  const loadMore = () => {
    if (page < totalPages) {
      setPage(prevPage => prevPage + 1);
    }
  };

  if (loading) return <div className="text-center mt-8">Loading...</div>;
  if (error) return <div className="text-center mt-8 text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <div className="flex-1 p-4 pl-20">
        <TopNavbar />
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Validator Overview</h1>
            <div className="flex items-center">
              <span className="mr-2">Consensus Layer</span>
              <Toggle 
                checked={layer === 'el'}
                onChange={() => {
                  setLayer(prevLayer => prevLayer === 'cl' ? 'el' : 'cl');
                  setPage(1);
                  setClValidators([]);
                  setElValidators([]);
                  setMissedBlocksData([]);
                }}
              />
              <span className="ml-2">Execution Layer</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard 
              name={`Latest Block Height (${layer === 'cl' ? 'Consensus' : 'Execution'} Layer)`} 
              value={layer === 'cl' ? stats.latestBlockHeight.toLocaleString() : 'N/A'} 
              icon={Clock} 
            />
            <StatCard name="Total Validators" value={stats.totalValidators} icon={Users} />
            {layer === 'cl' ? (
              <StatCard name="Average Uptime" value={`${stats.averageUptime}%`} icon={Activity} />
            ) : (
              <StatCard name="Total BGT Staked" value={`${stats.totalBgtStaked.toLocaleString()} BGT`} icon={Database} />
            )}
          </div>

          {/* Add ValidatorSearch component for CL layer */}
          {layer === 'cl' && (
            <div className="mb-6">
              <ValidatorSearch />
            </div>
          )}

          <Card className="bg-gray-800 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
            <Card.Body>
              {layer === 'cl' ? (
                <>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-700">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Validator</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Uptime (24h)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Latest Signed Blocks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {clValidators.map((validator) => {
                        const missedBlockInfo = missedBlocksData.find(v => v.address === validator.address);
                        const uptime24h = missedBlockInfo ? (100 - missedBlockInfo.missed_percentage) : 0;
                        return (
                          <tr key={validator.address} className="hover:bg-gray-700 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {validator.logo_uri && (
                                  <img 
                                    src={validator.logo_uri} 
                                    alt={`${validator.name} logo`} 
                                    className="w-6 h-6 mr-2 rounded-full object-contain bg-white"
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-logo.png'; }}
                                  />
                                )}
                                {validator.name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{validator.address}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {loadingMissedBlocks ? (
                                <span>Loading...</span>
                              ) : (
                                <div className="flex items-center">
                                  <div className="w-16 bg-gray-600 rounded-full h-2 mr-2">
                                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${uptime24h}%` }}></div>
                                  </div>
                                  {uptime24h.toFixed(2)}%
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <BlockUptimeSquares blocks={validator.recent_blocks} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {page < totalPages && (
                    <div className="mt-4 text-center">
                      <Button onClick={loadMore} color="primary" loading={loading}>
                        {loading ? 'Loading...' : 'Load More'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <ELValidatorsTable validators={elValidators} />
              )}
            </Card.Body>
          </Card>
          
          {lastUpdated && (
            <div className="mt-4 text-sm text-gray-400">
              Last updated: Block {lastUpdated.block_height} at {new Date(lastUpdated.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidatorsPage;